import React from 'react';
import styled from 'styled-components';
import { Button } from '../components/Button';
import { useNavigate } from 'react-router-dom';
import { theme } from '../styles/GlobalStyles';
import { Heading } from '../components/Heading';
import { motion } from 'framer-motion';
import { FaRocket, FaUsers, FaGlobe, FaChartLine } from 'react-icons/fa';

const HomeContainer = styled.div`
  width: 100%;
  overflow-x: hidden;
`;

const HeroSection = styled.section`
  position: relative;
  height: 100vh;
  padding-top: 100px; /* Account for standard navbar */
  background: linear-gradient(135deg, rgba(0, 0, 0, 0.3), rgba(0, 0, 0, 0.1)),
    url('/img_to_use/home page main.jpg') center/cover;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  text-align: center;
`;

const HeroContent = styled.div`
  max-width: 900px;
  z-index: 2;
  padding: 0 2rem;
`;

const HeroTitle = styled(motion.h1)`
  font-size: 4.5rem;
  font-weight: 700;
  margin-bottom: 1.5rem;
  text-shadow: 2px 2px 8px rgba(0, 0, 0, 0.6), 0 0 12px ${theme.colors.accent}33;
  color: ${theme.colors.accent};
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    font-size: 2.8rem;
  }
`;

const HeroSubtitle = styled(motion.p)`
  font-size: 1.6rem;
  margin-bottom: 2.5rem;
  font-weight: 300;
  text-shadow: 1px 1px 6px rgba(0, 0, 0, 0.8);
  color: #FFFFFF; /* Keep subtitle white for better readability */
  opacity: 0.95;
`;

const HeroButtons = styled(motion.div)`
  display: flex;
  gap: 1.5rem;
  justify-content: center;
  flex-wrap: wrap;
`;

const StatsSection = styled.section`
  background: linear-gradient(135deg, #f8f9fa, white);
  padding: 5rem 0;
`;

const StatsGrid = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 3rem;
  padding: 0 2rem;
`;

const StatCard = styled(motion.div)`
  text-align: center;
  padding: 3rem 2rem;
  background: white;
  border-radius: 20px;
  box-shadow: 0 15px 35px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  border: 1px solid #e9ecef;

  &:hover {
    transform: translateY(-15px);
    box-shadow: 0 25px 50px rgba(0, 0, 0, 0.15);
  }
`;

const StatIcon = styled.div`
  font-size: 3rem;
  color: ${theme.colors.primary};
  margin-bottom: 1rem;
`;

const StatNumber = styled.h3`
  font-size: 3.5rem;
  font-weight: 700;
  color: ${theme.colors.primary};
  margin-bottom: 0.5rem;
`;

const StatLabel = styled.p`
  font-size: 1.2rem;
  color: ${theme.colors.text};
  font-weight: 500;
`;

const SectionSeparator = styled.div`
  width: 100%;
  height: 4px;
  background: linear-gradient(90deg, 
    transparent 0%, 
    ${theme.colors.secondary} 20%, 
    ${theme.colors.accent} 50%, 
    ${theme.colors.secondary} 80%, 
    transparent 100%
  );
  margin: 0;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 60px;
    height: 60px;
    background: ${theme.colors.accent};
    border-radius: 50%;
    box-shadow: 0 0 20px rgba(244, 164, 96, 0.3);
  }
  
  &::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 30px;
    height: 30px;
    background: ${theme.colors.background};
    border-radius: 50%;
  }
`;

const SectorsSection = styled.section`
  background: ${theme.colors.sectionAlt};
  padding: 6rem 0;
`;

const SectorsContent = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const SectorsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 2rem;
  margin-top: 3rem;
  max-width: 1400px;
  margin-left: auto;
  margin-right: auto;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 1.5rem;
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
`;

const SectorCard = styled(motion.div)`
  position: relative;
  height: 250px;
  border-radius: 15px;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 15px 40px rgba(0, 0, 0, 0.2);
  }
`;

const SectorImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;

  ${SectorCard}:hover & {
    transform: scale(1.05);
  }
`;

const SectorOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(45deg, rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.3));
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
`;

const SectorTitle = styled.h3`
  color: ${theme.colors.accent};
  font-size: 1.5rem;
  font-weight: 700;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.5);
  padding: 0 1rem;
  line-height: 1.3;
`;

const WhyChooseSection = styled.section`
  background: white;
  padding: 6rem 0;
`;

const MissionVisionSection = styled.section`
  background: #f8f9fa;
  padding: 6rem 0;
`;

const MissionVisionContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const MissionVisionGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;

  @media (max-width: 968px) {
    grid-template-columns: 1fr;
    gap: 3rem;
  }
`;

const MissionCard = styled(motion.div)`
  background: white;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 15px 35px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 25px 50px rgba(0, 0, 0, 0.15);
  }
`;

const VisionCard = styled(motion.div)`
  background: white;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 15px 35px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 25px 50px rgba(0, 0, 0, 0.15);
  }
`;

const MissionImage = styled.img`
  width: 100%;
  height: 250px;
  object-fit: cover;
`;

const VisionImage = styled.img`
  width: 100%;
  height: 250px;
  object-fit: cover;
`;

const MissionContent = styled.div`
  padding: 2rem;

  h3 {
    font-size: 1.8rem;
    color: ${theme.colors.primary};
    margin-bottom: 1rem;
    font-weight: 700;
  }

  p {
    font-size: 1.1rem;
    line-height: 1.7;
    color: ${theme.colors.text};
  }
`;

const VisionContent = styled.div`
  padding: 2rem;

  h3 {
    font-size: 1.8rem;
    color: ${theme.colors.primary};
    margin-bottom: 1rem;
    font-weight: 700;
  }

  p {
    font-size: 1.1rem;
    line-height: 1.7;
    color: ${theme.colors.text};
  }
`;

const WhyChooseContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  align-items: center;
  padding: 0 2rem;

  @media (max-width: ${theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const WhyChooseText = styled.div`
  h2 {
    font-size: 2.8rem;
    margin-bottom: 1.5rem;
    color: ${theme.colors.primary};
    font-weight: 700;
  }

  p {
    font-size: 1.2rem;
    line-height: 1.8;
    color: ${theme.colors.text};
    margin-bottom: 2rem;
  }
`;

const WhyChooseFeatures = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const FeatureItem = styled(motion.div)`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1.5rem;
  background: linear-gradient(135deg, #f8f9fa, #e9ecef);
  border-radius: 15px;
  transition: all 0.3s ease;

  &:hover {
    transform: translateX(10px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
  }

  .feature-icon {
    font-size: 2rem;
    color: ${theme.colors.primary};
  }

  .feature-text {
    h4 {
      font-size: 1.2rem;
      margin-bottom: 0.5rem;
      color: ${theme.colors.primary};
      font-weight: 600;
    }

    p {
      margin: 0;
      color: ${theme.colors.text};
      font-size: 0.95rem;
    }
  }
`;

const CTASection = styled.section`
  background: ${theme.colors.gradient};
  color: white;
  padding: 6rem 0;
  text-align: center;
`;

const CTAContent = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 0 2rem;

  h2 {
    font-size: 3rem;
    margin-bottom: 1.5rem;
    font-weight: 700;
  }

  p {
    font-size: 1.3rem;
    margin-bottom: 3rem;
    opacity: 0.95;
    line-height: 1.7;
  }
`;

const CTAButtons = styled.div`
  display: flex;
  gap: 1.5rem;
  justify-content: center;
  flex-wrap: wrap;
`;

const TestimonialSection = styled.section`
  background: linear-gradient(135deg, #f8f9fa, white);
  padding: 6rem 0;
`;

const TestimonialContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
  text-align: center;
`;

const TestimonialGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 2.5rem;
  margin-top: 3rem;
`;

const TestimonialCard = styled(motion.div)`
  background: white;
  padding: 3rem 2rem;
  border-radius: 20px;
  box-shadow: 0 15px 35px rgba(0, 0, 0, 0.1);
  border: 1px solid #e9ecef;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 25px 50px rgba(0, 0, 0, 0.15);
  }

  .testimonial-text {
    font-style: italic;
    font-size: 1.1rem;
    line-height: 1.7;
    margin-bottom: 2rem;
    color: ${theme.colors.text};
  }

  .testimonial-author {
    font-weight: 600;
    color: ${theme.colors.primary};
    margin-bottom: 0.5rem;
  }

  .testimonial-company {
    color: #666;
    font-size: 0.9rem;
  }
`;

const HomePage: React.FC = () => {
  const navigate = useNavigate();

  const stats = [
    { 
      icon: <FaUsers />, 
      number: '2500+', 
      label: 'Successful Placements' 
    },
    { 
      icon: <FaGlobe />, 
      number: '50+', 
      label: 'Countries Served' 
    },
    { 
      icon: <FaChartLine />, 
      number: '98%', 
      label: 'Client Satisfaction' 
    },
    { 
      icon: <FaRocket />, 
      number: '15+', 
      label: 'Years Experience' 
    }
  ];

  const sectors = [
    {
      slug: 'oil-gas',
      title: 'Oil & Gas',
      image: '/img_to_use/oil and gas.jpg'
    },
    {
      slug: 'fmcg',
      title: 'FMCG',
      image: '/img_to_use/FMCG.jpg'
    },
    {
      slug: 'infrastructure',
      title: 'Infrastructure',
      image: '/img_to_use/Infrastructure.jpg'
    },
    {
      slug: 'engineering-procurement-construction',
      title: 'Engineering Procurement & Construction',
      image: '/img_to_use/Engineering Procurement & Construction.jpg'
    },
    {
      slug: 'it-staffing',
      title: 'IT Staffing',
      image: '/img_to_use/campaign-creators-8F4EX4Nw1yY-unsplash.jpg'
    },
    {
      slug: 'healthcare-staffing',
      title: 'Healthcare Staffing',
      image: '/img_to_use/helthcare.jpg'
    },
    {
      slug: 'logistics-supply-chain',
      title: 'Logistics & Supply Chain Staffing',
      image: '/img_to_use/logistics.jpg'
    },
    {
      slug: 'engineering-epc-staffing',
      title: 'Engineering & EPC Staffing',
      image: '/img_to_use/home page enginner 1.jpg'
    }
  ];

  const features = [
    {
      icon: <FaGlobe />,
      title: 'Global Network',
      description: 'Access to talent pools across multiple continents'
    },
    {
      icon: <FaChartLine />,
      title: 'Data-Driven Approach',
      description: 'Advanced analytics and insights for better hiring decisions'
    },
    {
      icon: <FaUsers />,
      title: 'Expert Team',
      description: 'Industry specialists with deep domain knowledge'
    },
    {
      icon: <FaRocket />,
      title: 'Fast Turnaround',
      description: 'Quick placement with quality candidates'
    }
  ];

  const testimonials = [
    {
      text: "De ARC Global transformed our hiring process. Their attention to detail and understanding of our technical needs resulted in perfect matches for our software development positions.",
      author: "Sarah Johnson",
      company: "Tech Innovations Inc."
    },
    {
      text: "The global reach and expertise of De ARC Global helped us expand our operations internationally with the right healthcare talent in key positions.",
      author: "Dr. Michael Chen",
      company: "Global Healthcare Systems"
    },
    {
      text: "Outstanding service and results. De ARC Global's team understood our unique engineering requirements and delivered exceptional candidates within our project timeline.",
      author: "Emily Rodriguez",
      company: "Infrastructure Solutions Ltd."
    }
  ];

  return (
    <HomeContainer>
      <HeroSection>
        <HeroContent>
          <HeroTitle
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            De ARC Global
          </HeroTitle>
          <HeroSubtitle
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            Your trusted partner in global staffing and recruitment solutions. 
            Connecting exceptional talent with extraordinary opportunities worldwide. 
          </HeroSubtitle>
          <HeroButtons
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            <Button variant="primary" size="large" onClick={() => navigate('/contact')}>
              Get Started
            </Button>
          </HeroButtons>
        </HeroContent>
      </HeroSection>

      <StatsSection>
        <StatsGrid>
          {stats.map((stat, index) => (
            <StatCard
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
            >
              <StatIcon>{stat.icon}</StatIcon>
              <StatNumber>{stat.number}</StatNumber>
              <StatLabel>{stat.label}</StatLabel>
            </StatCard>
          ))}
        </StatsGrid>
      </StatsSection>

      <SectionSeparator />

      <SectorsSection>
        <SectorsContent>
          <Heading level="h2" align="center" style={{ color: '#888', marginBottom: '3rem' }}>Sectors We Serve</Heading>
          <SectorsGrid>
            {sectors.map((sector, index) => (
              <SectorCard
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                onClick={() => navigate(`/sectors/${sector.slug}`, { state: { from: '/' } })}
              >
                <SectorImage src={sector.image} alt={sector.title} />
                <SectorOverlay>
                  <SectorTitle>{sector.title}</SectorTitle>
                </SectorOverlay>
              </SectorCard>
            ))}
          </SectorsGrid>
        </SectorsContent>
      </SectorsSection>

      <SectionSeparator />

      <WhyChooseSection>
        <WhyChooseContent>
          <WhyChooseText>
            <motion.h2
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              Why Choose De ARC Global?
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              viewport={{ once: true }}
            >
              With over 15 years of experience in global staffing and recruitment, De ARC Global has built a reputation 
              for delivering exceptional results across IT, Healthcare, Engineering, and Logistics sectors. 
              Our comprehensive approach combines deep industry expertise with innovative technology to find the perfect match for your organization.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              viewport={{ once: true }}
            >
              <Button variant="primary" size="large" onClick={() => navigate('/about')}>
                Learn About Us
              </Button>
            </motion.div>
          </WhyChooseText>
          <WhyChooseFeatures>
            {features.map((feature, index) => (
              <FeatureItem
                key={index}
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <div className="feature-icon">{feature.icon}</div>
                <div className="feature-text">
                  <h4>{feature.title}</h4>
                  <p>{feature.description}</p>
                </div>
              </FeatureItem>
            ))}
          </WhyChooseFeatures>
        </WhyChooseContent>
      </WhyChooseSection>

      <MissionVisionSection>
        <MissionVisionContent>
          <Heading level="h2" align="center" style={{ color: '#888', marginBottom: '4rem' }}>Our Mission & Vision</Heading>
          <MissionVisionGrid>
            <MissionCard
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <MissionImage src="/img_to_use/mission (1).jpg" alt="Our Mission" />
              <MissionContent>
                <h3>Our Mission</h3>
                <p>To bridge the gap between exceptional talent and remarkable opportunities across the globe, empowering organizations with the right human capital while helping professionals achieve their career aspirations.</p>
              </MissionContent>
            </MissionCard>
            <VisionCard
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <VisionImage src="/img_to_use/vision.jpg" alt="Our Vision" />
              <VisionContent>
                <h3>Our Vision</h3>
                <p>To become the world's most trusted partner in global staffing solutions, known for our integrity, innovation, and commitment to creating meaningful connections that transform businesses and lives.</p>
              </VisionContent>
            </VisionCard>
          </MissionVisionGrid>
        </MissionVisionContent>
      </MissionVisionSection>

      <SectionSeparator />

      <TestimonialSection>
        <TestimonialContent>
          <Heading level="h2" align="center">What Our Clients Say</Heading>
          <TestimonialGrid>
            {testimonials.map((testimonial, index) => (
              <TestimonialCard
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <div className="testimonial-text">"{testimonial.text}"</div>
                <div className="testimonial-author">{testimonial.author}</div>
                <div className="testimonial-company">{testimonial.company}</div>
              </TestimonialCard>
            ))}
          </TestimonialGrid>
        </TestimonialContent>
      </TestimonialSection>

      <SectionSeparator />

      <CTASection>
        <CTAContent>
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            Ready to Transform Your Hiring?
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true }}
          >
            Let's discuss how De ARC Global can help you build the perfect team. 
            Contact us today for a consultation tailored to your specific needs.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            viewport={{ once: true }}
          >
            <CTAButtons>
              <Button variant="primary" size="large" onClick={() => navigate('/contact')}>
                Get In Touch
              </Button>
              <Button variant="secondary" size="large" onClick={() => navigate('/services')}>
                View Services
              </Button>
            </CTAButtons>
          </motion.div>
        </CTAContent>
      </CTASection>
    </HomeContainer>
  );
};

export default HomePage;
 